import { Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { TFunction } from "i18next";
import { formatNumber } from "../../../../../../common/utils";
import { FiltersDashboardPayload } from "../../../../../../context/FiltersProvider";
import { FiltersRegistry } from "../../../../../../context/FiltersProvider/types";
import {
	ConsumptionPageDTO,
	ConsumptionPageItemDTO,
	FilterCombinationPayload,
	FilterPayload,
	NodeDetailsDTO,
} from "../../../../../../datasources/api-client/data-contracts";
import { ThemeColors } from "../../../../../../theme/themeValues";

/**
 * utility function used in TransportGreenrouterDetail to prepare data for rendering using MultiValueWidget
 * @param data TripDetailsDTO object fetched from the backend
 * @param tFunc callback to *t* function (from i18next)
 * @returns object containing transportData and transportTotalEmissions display values, ready to use with
 * the MultiValueWidget
 */
export const displayValues = (
	data: NodeDetailsDTO | undefined,
	tFunc: TFunction,
	appliedFiltersPayload?: FiltersDashboardPayload,
	menuFilters?: FiltersRegistry | null
) => {
	/**
	 * Example representation of a DV (displayableValue)
	 * {
	 * 		label: string,
	 * 		value: string,
	 * 		measure: string,
	 * 		reqExpand: boolean
	 * }
	 */

	/* -----------------------------
	 * Personal information
	 * ----------------------------- */
	const personalInformation = data
		? [
				{
					label: tFunc("greenrouterDetail/code gln"),
					value: data.code || "-",
					measure: "",
					reqExpand: false,
				},
				{
					label: tFunc("greenrouterDetail/type of site"),
					value: data.typeDescription || "-",
					measure: "",
					reqExpand: false,
				},
				{
					label: tFunc("greenrouterDetail/site name"),
					value: data.description || "-",
					measure: "",
					reqExpand: false,
				},
				{
					label: tFunc("greenrouterDetail/addres"),
					value: data.address || "",
					measure: "",
					reqExpand: false,
				},
				{
					label: tFunc("greenrouterDetail/country"),
					value: data.countryDescription || "",
					measure: "",
					reqExpand: false,
				},
				{
					label: tFunc("greenrouterDetail/city"),
					value: data.city || "",
					measure: "",
					reqExpand: false,
				},
				{
					label: tFunc("greenrouterDetail/postal code"),
					value: data.zipCode || "",
					measure: "",
					reqExpand: false,
				},
				{
					label: tFunc("greenrouterDetail/province"),
					value: data.country || "",
					measure: "",
					reqExpand: false,
				},
				{
					label: tFunc("greenrouterDetail/latitude"),
					value: formatNumber(data.lat, "decimal", 0, 6),
					measure: "",
					reqExpand: false,
				},
				{
					label: tFunc("greenrouterDetail/longitude"),
					value: formatNumber(data.lng, "decimal", 0, 6),
					measure: "",
					reqExpand: false,
				},
			]
		: [];

	/* -----------------------------
	 * Node filters (data.filters)
	 * ----------------------------- */
	// const filters = data?.filters
	// 	? Object.entries(data.filters).map(([key, value]) => ({
	// 			label: key,
	// 			value: String(value ?? "-"),
	// 			measure: "",
	// 			reqExpand: true,
	// 		}))
	// 	: [];

	/* -----------------------------
	 * General data
	 * ----------------------------- */
	const generalData = data
		? [
				{
					label: tFunc("greenrouterDetail/covered area"),
					value: data.area ? formatNumber(data.area?.value) : "-",
					measure: "m2",
					reqExpand: false,
				},
				{
					label: tFunc("greenrouterDetail/capacity"),
					value: data.capacity ? formatNumber(data.capacity) : "-",
					measure: "",
					reqExpand: false,
				},
				{
					label: tFunc("greenrouterDetail/temperature"),
					value: data.temperature ? formatNumber(data.temperature?.value) : "-",
					measure: "°C",
					reqExpand: false,
				},
				{
					label: tFunc("greenrouterDetail/year of construction"),
					value: data.constructionYear ? formatNumber(data.constructionYear) : "-",
					measure: "",
					reqExpand: false,
				},
				{
					label: tFunc("greenrouterDetail/site activity"),
					value: data.activityDescription || "-",
					measure: "",
					reqExpand: true,
				},
				{
					label: tFunc("greenrouterDetail/value-added services"),
					value: data.valueAddedServiceDescription || "-",
					measure: "",
					reqExpand: true,
				},
				{
					label: tFunc("greenrouterDetail/consider travel emissions calculation"),
					value: data.computeFlagDescription ?? "-",
					measure: "",
					reqExpand: true,
				},
				...Object.entries(data.filters ?? {}).map(([key, filter]: [string, any]) => ({
					label: key,
					value: String(filter ?? "-"),
					measure: "",
					reqExpand: true,
				})),
			]
		: [];

	/* -----------------------------
	 * Total emissions
	 * ----------------------------- */
	const totalEmissions = data
		? [
				{
					label: tFunc("greenrouterDetail/total emissions"),
					value: formatNumber(data.totalEmission.value),
					measure: data.totalEmission.measure,
					reqExpand: false,
				},
				{
					label: tFunc("greenrouterDetail/emission intensity"),
					value: formatNumber(data.intensityEmission.value),
					measure: data.intensityEmission.measure,
					reqExpand: false,
				},
				{
					label: tFunc("greenrouterDetail/biogenic emissions"),
					value: formatNumber(data.biogenicEmission.value),
					measure: data.biogenicEmission.measure,
					reqExpand: false,
				},
			]
		: [];

	/* -----------------------------
	 * Applied filters
	 * ----------------------------- */
	function isFilterPayload(filter: FilterCombinationPayload | FilterPayload): filter is FilterPayload {
		return (filter as FilterPayload).id !== undefined;
	}

	function resolveOptionValue(
		value: string | number | (string | number)[] | undefined,
		options: { id: string | number; label: string }[]
	): string {
		if (value === undefined || value === null) return "";

		const resolve = (v: string | number) => options.find((opt) => opt.id === v)?.label ?? String(v);

		if (Array.isArray(value)) {
			return value.map(resolve).join(", ");
		}

		return resolve(value);
	}

	function normalizeFilters(filters: (FilterCombinationPayload | FilterPayload)[] = []): any[] {
		return filters.flatMap((filter) => {
			if (isFilterPayload(filter)) {
				const menuFilter = menuFilters?.byId[filter.id];

				const operatorLabel =
					menuFilter?.field?.operators.find((op) => op.operator === filter.operator)?.label ??
					filter.operator;

				return [
					{
						label: menuFilter?.name ?? "Unknown Filter",
						value: resolveOptionValue(filter.value, menuFilter?.field?.options ?? []),
						measure: operatorLabel,
						reqExpand: false,
					},
				];
			}

			return normalizeFilters(filter.filters ?? []);
		});
	}

	const appliedFiltersToDetail = normalizeFilters(appliedFiltersPayload?.filters);

	return {
		personalInformation,
		generalData,
		totalEmissions,
		appliedFiltersToDetail,
	};
};

export const getConsumptionRows = (data: ConsumptionPageDTO, t: TFunction) => {
	if (!data) return [];

	return data.items.map((item: ConsumptionPageItemDTO) => {
		const safeNumber = (val: any): number => {
			if (val === null || val === undefined || val === "" || isNaN(Number(val))) {
				return 0;
			}
			return Number(val);
		};

		return {
			id: item.id,
			period:
				item.startDate && item.endDate
					? `${dayjs(item.startDate).format("DD/MM/YYYY")} - ${dayjs(item.endDate).format("DD/MM/YYYY")}`
					: "-",
			detail: item.elementLabel,
			consumption: safeNumber(item.consumption.value),
			measure: item.consumption.measure,
			emissions: item.emissions.value,
		};
	});
};

export const consumptionsCols: GridColDef[] = [
	{
		field: "id",
		headerName: "ID",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "period",
		headerName: "period",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "detail",
		headerName: "detail",
		flex: 0.8,
		sortable: false,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "consumption",
		headerName: "consumption",
		flex: 0.8,
		sortable: false,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="flex-end" justifyContent="center" textAlign={"right"}>
					<Typography variant="body2">{formatNumber(params.value, "decimal", 2, 2)}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "measure",
		headerName: "measure",
		flex: 0.5,
		sortable: false,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "emissions",
		headerName: "emissions",
		flex: 0.8,
		sortable: false,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="flex-end" justifyContent="center">
					<Typography variant="body2">{formatNumber(params.value, "decimal", 2, 2)}</Typography>
				</Stack>
			);
		},
	},
];

export const getConsumptionsColumns = (dataCol: GridColDef[], t: TFunction) =>
	dataCol.map((col, index) => {
		const label = col.headerName ? t(`greenrouterList/${col.headerName}`) : "";

		return {
			...col,
			headerName: label,
			renderHeader: () =>
				label && (
					<Stack direction="column" width="100%">
						<Stack
							justifyContent={index === 3 || index === 5 ? "right" : "start"}
							alignContent="start"
							flexDirection="row"
							alignItems="baseline"
						>
							<Typography fontWeight={700} sx={{ color: ThemeColors.LightContrastText }} variant="body2">
								{label}
							</Typography>
							<Typography
								fontWeight={700}
								sx={{ color: ThemeColors.LightContrastText }}
								variant="caption"
								ml={0.5}
							>
								{index === 5 && "tCO2e"}
							</Typography>
						</Stack>
					</Stack>
				),
		};
	});
